import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  SIDEBAR_READY,
  activeWorkspace,
  clampSidebarWidth,
  setWorkspaceSidebar,
  workspaceSidebar,
  type SidebarState,
  type SidebarTab,
} from "@helm/core";
import { useWorkspaces, type WorkspaceStore } from "../workspace/workspaceStore";
import { lazyComponent } from "../components/lazy";
import { pushToast } from "../components/Toasts";
import { loadSymbol, useActiveChart } from "../toolbar/activeChart";
import { WatchlistView } from "./WatchlistView";
import { DetailsView } from "./DetailsView";
import { onOpenSidebarTab } from "./events";
import { useWatchlistDoc } from "./watchlistStore";
import "./sidebar.css";

const AlertsPanel = lazyComponent(() => import("../widgets/Alerts").then((m) => m.AlertsPanel), "alerts");

/** Right-bar icons (16 px, stroke, currentColor), local so the shared icon set stays untouched. */
const ICONS: Record<SidebarTab | "popout" | "dock", string> = {
  watchlist: "M5.5 4h8M5.5 8h8M5.5 12h8M2.5 4h.01M2.5 8h.01M2.5 12h.01",
  details: "M8 1.8a6.2 6.2 0 1 0 0 12.4A6.2 6.2 0 0 0 8 1.8zM8 7.2v4.3M8 4.8v.01",
  alerts: "M4 11V7a4 4 0 0 1 8 0v4l1 1.5H3zM6.5 14h3",
  objects: "M2.5 3h4M4.5 3v10M4.5 7.5h4M4.5 12.5h4M9.5 6h4v3h-4zM9.5 11h4v3h-4z",
  notes: "M3 2.5h7l3 3v8H3zM10 2.5v3h3",
  popout: "M9 2.5h4.5V7M13.5 2.5L8 8M11.5 9.5v4h-9v-9h4",
  dock: "M2.5 2.5h11v11h-11zM9.5 2.5v11",
};

function SideIcon({ name }: { name: keyof typeof ICONS }): React.JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={ICONS[name]} />
    </svg>
  );
}

const TABS: { tab: SidebarTab; label: string; hint: string }[] = [
  {
    tab: "watchlist",
    label: "Watchlist",
    hint: "Watchlist — lists, sections, flags; click a row to load it",
  },
  { tab: "details", label: "Details", hint: "Symbol details" },
  { tab: "alerts", label: "Alerts", hint: "Alerts — log, rules, watchers" },
  { tab: "objects", label: "Object tree", hint: "Object tree — arrives with the drawing tools" },
  { tab: "notes", label: "Notes", hint: "Notes — later" },
];

/**
 * Right sidebar (owner 2026-09-28; TradingView's right toolbar / widget bar): a slim icon bar on
 * the right edge; an icon opens its panel, clicking it again closes it. The panel is resizable
 * (drag its left edge; double-click resets) and its open state, width and tab are remembered per
 * workspace (`layout/desktop`). It can pop out into its own window (same JS context as the main
 * window, like dockview pop-outs: the md hub, stores and chart targets keep working); closing that
 * window docks it back.
 */
export function RightSidebar({
  store,
  onPopoutWindow,
}: {
  store: WorkspaceStore;
  /** Route the pop-out window's keys through the shell (hotkeys); returns a cleanup. */
  onPopoutWindow?: (win: Window) => () => void;
}): React.JSX.Element {
  const { doc } = useWorkspaces(store);
  const ws = activeWorkspace(doc);
  const saved = workspaceSidebar(ws);
  const [dragW, setDragW] = useState<number | null>(null);
  const [popped, setPopped] = useState<Window | null>(null);
  const width = dragW ?? saved.width;

  const save = useCallback(
    (next: SidebarState): void => {
      store.update((d) => setWorkspaceSidebar(d, activeWorkspace(d).id, next));
    },
    [store],
  );

  useEffect(
    () =>
      onOpenSidebarTab((tab) => {
        const cur = workspaceSidebar(activeWorkspace(store.doc));
        if (popped) {
          save({ ...cur, tab });
          popped.focus();
        } else save({ ...cur, open: true, tab });
      }),
    [store, save, popped],
  );

  const startResize = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (e.button !== 0) return;
    e.preventDefault();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    const x0 = e.clientX;
    const w0 = saved.width;
    let latest = w0;
    const move = (ev: PointerEvent): void => {
      latest = clampSidebarWidth(w0 + (x0 - ev.clientX));
      setDragW(latest);
    };
    const up = (): void => {
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", up);
      setDragW(null);
      if (latest !== w0) save({ ...workspaceSidebar(activeWorkspace(store.doc)), width: latest });
    };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
  };

  const popOut = (): void => {
    const w = window.open(
      "/popout.html",
      "helm-sidebar",
      `popup,width=${String(Math.max(320, saved.width + 24))},height=${String(Math.min(900, window.innerHeight))}`,
    );
    if (!w) {
      pushToast({
        tone: "warn",
        title: "Pop-out blocked",
        body: "Allow pop-ups for TradePrizm in the browser, then try again.",
      });
      return;
    }
    const ready = (): void => {
      preparePopout(w);
      setPopped(w);
    };
    if (w.document.readyState === "complete" && w.location.href !== "about:blank") ready();
    else w.addEventListener("load", ready, { once: true });
  };

  // Pop-out lifetime: mirror theme + styles, route keys, dock back when it closes.
  useEffect(() => {
    if (!popped) return;
    const offKeys = onPopoutWindow?.(popped);
    const mirror = new MutationObserver(() => {
      mirrorTheme(popped);
    });
    mirror.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme", "data-style", "style"],
    });
    const styles = new MutationObserver((recs) => {
      for (const r of recs) for (const n of r.addedNodes) copyStyleNode(n, popped);
    });
    styles.observe(document.head, { childList: true });
    const closed = (): void => {
      setPopped(null);
    };
    popped.addEventListener("pagehide", closed);
    const poll = setInterval(() => {
      if (popped.closed) closed();
    }, 500);
    const closeWithMain = (): void => {
      popped.close();
    };
    window.addEventListener("pagehide", closeWithMain);
    return () => {
      offKeys?.();
      mirror.disconnect();
      styles.disconnect();
      clearInterval(poll);
      popped.removeEventListener("pagehide", closed);
      window.removeEventListener("pagehide", closeWithMain);
    };
  }, [popped, onPopoutWindow]);

  // Close the pop-out if the shell goes away (sign-out).
  useEffect(
    () => () => {
      popped?.close();
    },
    [popped],
  );

  const panel = (
    <SidebarPanel
      tab={saved.tab}
      popped={popped !== null}
      onTab={(t) => {
        save({ ...saved, tab: t });
      }}
      onClose={() => {
        if (popped) popped.close();
        else save({ ...saved, open: false });
      }}
      onPopOut={popOut}
    />
  );

  return (
    <div className={saved.open && !popped ? "rsb open" : "rsb"}>
      {saved.open && !popped && (
        <aside className="rsb-panel" style={{ width }} aria-label="Right sidebar">
          <div
            className={dragW !== null ? "rsb-resize active" : "rsb-resize"}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize sidebar"
            aria-valuemin={220}
            aria-valuemax={640}
            aria-valuenow={width}
            title="Drag to resize · double-click for the default width"
            onPointerDown={startResize}
            onDoubleClick={() => {
              save({ ...saved, width: 300 });
            }}
          />
          {panel}
        </aside>
      )}
      {popped && createPortal(<div className="rsb-popout">{panel}</div>, popped.document.body)}
      {/*
       * No icon rail (owner 2026-10-04): Watchlist, Symbol details and Alerts are "+ Add module"
       * modules; the panel still opens from the toolbar's Alert button and Ctrl+K "Right sidebar:".
       */}
    </div>
  );
}

function SidebarPanel({
  tab,
  popped,
  onTab,
  onClose,
  onPopOut,
}: {
  tab: SidebarTab;
  popped: boolean;
  onTab: (t: SidebarTab) => void;
  onClose: () => void;
  onPopOut: () => void;
}): React.JSX.Element {
  const chart = useActiveChart();
  const chartSymbol = chart?.snapshot.symbol ?? null;
  const [picked, setPicked] = useState<string | null>(null);
  const doc = useWatchlistDoc();
  const firstInList =
    doc.lists
      .find((l) => l.id === doc.activeId)
      ?.items.flatMap((it) => (it.type === "symbol" ? [it.symbol] : []))[0] ?? null;
  const detailsSymbol = chartSymbol ?? picked ?? firstInList;
  const title = TABS.find((t) => t.tab === tab)?.label ?? "";
  return (
    <div className="rsb-inner">
      <div className="rsb-head">
        {popped ? (
          <div className="rsb-tabs" role="tablist" aria-label="Sidebar">
            {TABS.filter((t) => SIDEBAR_READY.has(t.tab)).map((t) => (
              <button
                key={t.tab}
                type="button"
                role="tab"
                aria-selected={t.tab === tab}
                className={t.tab === tab ? "rsb-tab active" : "rsb-tab"}
                onClick={() => {
                  onTab(t.tab);
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        ) : (
          <span className="rsb-title">{title}</span>
        )}
        <span className="spacer" />
        {!popped && (
          <button
            type="button"
            className="icon-btn icon-btn-xs"
            aria-label="Pop out into its own window"
            title="Pop out into its own window"
            onClick={onPopOut}
          >
            <SideIcon name="popout" />
          </button>
        )}
        <button
          type="button"
          className="icon-btn icon-btn-xs"
          aria-label={popped ? "Dock back" : "Close the sidebar"}
          title={popped ? "Dock back into the main window" : "Close (the icon bar stays)"}
          onClick={onClose}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 16 16"
            aria-hidden="true"
            stroke="currentColor"
            strokeWidth="1.6"
          >
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </div>
      <div className="rsb-body">
        {tab === "watchlist" && (
          <WatchlistView
            onPick={(s) => {
              setPicked(s);
              const where = loadSymbol(s);
              if (where === "link" && !chart)
                pushToast({
                  tone: "info",
                  title: `${s} → red link group`,
                  body: "No chart in this workspace: widgets on the red link colour follow instead.",
                });
            }}
            selected={chartSymbol ?? picked}
            pickHint={
              chart
                ? "Click (or ↑/↓) to load into the active chart and its link colour"
                : "Click to load into the red link group (no chart open)"
            }
          />
        )}
        {tab === "details" && <DetailsView symbol={detailsSymbol} />}
        {tab === "alerts" && <AlertsPanel symbol={chartSymbol} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Pop-out window plumbing (same pattern as the dockview pop-outs in Workspace.tsx).

function mirrorTheme(win: Window): void {
  try {
    const src = document.documentElement;
    const dst = win.document.documentElement;
    for (const a of ["data-theme", "data-style", "style", "lang"]) {
      const v = src.getAttribute(a);
      if (v === null) dst.removeAttribute(a);
      else dst.setAttribute(a, v);
    }
    win.document.body.classList.add("popout-body");
  } catch {
    /* window closing */
  }
}

function copyStyleNode(n: Node, win: Window): void {
  const el = n as HTMLElement;
  if (el.nodeType !== 1) return;
  const tag = el.tagName;
  if (tag === "STYLE" || (tag === "LINK" && (el as HTMLLinkElement).rel === "stylesheet")) {
    win.document.head.appendChild(win.document.importNode(el, true));
  }
}

function preparePopout(win: Window): void {
  try {
    win.document.title = "TradePrizm · Sidebar";
    for (const n of document.head.querySelectorAll("style, link[rel='stylesheet']")) copyStyleNode(n, win);
    mirrorTheme(win);
  } catch {
    /* closed before load */
  }
}
